import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { DataSources } from '~src/datasource/resources/Datasources';
import { IFavorite } from '~src/favorites/types/IFavorite';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { getContentSpaceDefinition, getExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { SpaceInfoItemType } from '~src/spacesPlatform/models/ContentType';
import { ExploreContentApi_ContentType, isExploreContentApiContentType } from '~src/spacesPlatform/models/IContent';

export interface FavoriteRedirectRoute {
  // Route patterns with ':id' (content to open) and optionally ':idForFavoriting' (defaults to ':id').
  readonly paths: (urls: SpacesSiteUrls) => ReadonlyArray<string>;
  // Resolves false, or rejects, when the content no longer exists.
  readonly contentExists: (id: string) => Promise<boolean>;
  readonly contentPath: (id: string, urls: SpacesSiteUrls) => string;
}

export interface RemovedFavoriteError {
  readonly notFoundMessage: () => string;
  readonly backToContentMessage: () => string;
  readonly backToContentUrl: (urls: SpacesSiteUrls) => string;
}

export interface FavoriteRedirect {
  readonly favoriteUrl: (favorite: IFavorite, urls: SpacesSiteUrls) => string;
  // Shown when the favorite is removed because its content no longer exists.
  readonly removedFavoriteError?: RemovedFavoriteError;
  // Served by RedirectToFavoritedContentProvider. Omitted for view, which RedirectToViewProvider serves.
  readonly route?: FavoriteRedirectRoute;
}

// How a type's favorites differ from getDefaultFavoriteRedirect; each entry replaces only the fields it sets. Give a
// type its own removed-favorite copy here rather than inlining the type name into the generic copy: one shared string
// can't be translated (de "Diese Arbeitsmappe" vs "Dieses Projekt").
const typeSpecificFavoriteRedirects: Partial<Record<ExploreContentApi_ContentType, Partial<FavoriteRedirect>>> = {
  // Links straight to the collection, without a redirect.
  collection: {
    favoriteUrl: (favorite, urls) => urls.collectionUrl(favorite.objectLuid),
    removedFavoriteError: undefined,
    route: undefined,
  },
  // Favorited by a separate id for favoriting, and may have no datasource id when the datasource is gone.
  datasource: {
    favoriteUrl: (favorite, urls) => favorite.objectId === ''
      ? urls.favoriteDatasourceDoesntExistUrl(favorite.objectIdForFavoriting)
      : urls.favoriteDatasourceUrl(favorite.objectId, favorite.objectIdForFavoriting),
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteDatasourceCouldNotBeFound(),
      backToContentMessage: () => Messages.backToDatasources(),
      backToContentUrl: urls => urls.datasourcesUrl(),
    },
    route: {
      paths: urls => [
        urls.favoriteDatasourcePath(':id', ':idForFavoriting'),
        urls.favoriteDatasourceDoesntExistPath(':idForFavoriting'),
      ],
      contentExists: id => DataSources.all().withFilters([Filter.fieldEq('id', id)]).getFirst().then(content => !!content),
      contentPath: (id, urls) => urls.datasourcePath(id),
    },
  },
  flow: {
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteFlowCouldNotBeFound(),
      backToContentMessage: () => Messages.backToFlows(),
      backToContentUrl: urls => urls.flowsUrl(),
    },
  },
  project: {
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteProjectCouldNotBeFound(),
      backToContentMessage: () => Messages.backToProjects(),
      backToContentUrl: urls => urls.projectsUrl(),
    },
  },
  // Opened by RedirectToViewProvider.
  view: {
    favoriteUrl: (favorite, urls) => urls.redirectToFavoriteViewUrl(favorite.objectId),
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteViewCouldNotBeFound(),
      backToContentMessage: () => Messages.backToViews(),
      backToContentUrl: urls => urls.viewsUrl(),
    },
    route: undefined,
  },
  virtualConnection: {
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoritePublishedConnectionCouldNotBeFound(),
      backToContentMessage: () => Messages.backToPublishedConnections(),
      backToContentUrl: urls => urls.virtualConnectionsUrl(),
    },
  },
  // Virtual connection tables have no list page of their own, so link back to the virtual connections.
  virtualConnectionTable: {
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoritePublishedConnectionCouldNotBeFound(),
      backToContentMessage: () => Messages.backToPublishedConnections(),
      backToContentUrl: urls => urls.virtualConnectionsUrl(),
    },
  },
  workbook: {
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteWorkbookCouldNotBeFound(),
      backToContentMessage: () => Messages.backToWorkbooks(),
      backToContentUrl: urls => urls.workbooksUrl(),
    },
  },
};

// Builds the redirect from the type's content definitions, so a newly favoritable type needs no entry above.
export function getDefaultFavoriteRedirect(contentType: ExploreContentApi_ContentType): FavoriteRedirect | undefined {
  const explorableItemDefinition = getExplorableItemDefinition(contentType);
  const contentSpaceDefinition = getContentSpaceDefinition(contentType as SpaceInfoItemType);
  if (!explorableItemDefinition || !contentSpaceDefinition) {
    return undefined;
  }
  return {
    favoriteUrl: (favorite, urls) => urls.redirectToFavoriteUrl(contentType, favorite.objectId),
    removedFavoriteError: {
      notFoundMessage: () => Messages.favoriteContentCouldNotBeFound(),
      backToContentMessage: () => Messages.backToContent(),
      backToContentUrl: urls => urls.exploreUrl(),
    },
    route: {
      paths: urls => [urls.redirectToFavoritePath(contentType, ':id')],
      contentExists: id => explorableItemDefinition.getBasicQuery().withFilters([Filter.fieldEq('id', id)]).getFirst().then(content => !!content),
      contentPath: (id, urls) => {
        // contentSpaceUrl returns '/<query>#<path>'.
        const contentUrl = contentSpaceDefinition.contentSpaceUrl(urls, id, undefined);
        return contentUrl.substring(contentUrl.indexOf('#') + 1);
      },
    },
  };
}

// Accepts any type the server lists as favoritable (see filterFavoritableContentTypes), not only FavoriteItemType.
export function getFavoriteRedirect(contentType: string): FavoriteRedirect | undefined {
  if (!isExploreContentApiContentType(contentType)) {
    return undefined;
  }
  const defaultRedirect = getDefaultFavoriteRedirect(contentType);
  const typeSpecificRedirect = typeSpecificFavoriteRedirects[contentType];
  return typeSpecificRedirect ? _.extend({}, defaultRedirect, typeSpecificRedirect) : defaultRedirect;
}

// Path prefixes of every redirect route, e.g. '/redirect_to_favorite/'.
export function getFavoriteRedirectBasePaths(urls: SpacesSiteUrls): string[] {
  const typeSpecificPaths = _.flatten(_.map(typeSpecificFavoriteRedirects, redirect => redirect.route ? redirect.route.paths(urls) : []));
  return typeSpecificPaths
    .concat(urls.redirectToFavoritePath(':contentType', ':id'))
    .map(path => path.substring(0, path.indexOf(':')));
}
